<template>
    <AppHead title="Neue Veranstaltung eingeben" />
    <div class="lg:px-64">
        <PageTitle><span v-if="event">Veranstaltung bearbeiten</span><span v-else>Neue Veranstaltung eingeben</span> - Schritt 1 von 3</PageTitle>
        <div class="text-red-600">Bitte prüfen Sie vorher, ob Ihre Veranstaltung bereits im Kulturkalender vorhanden ist.</div>
        <form @submit.prevent="submit">
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="category" value="Rubrik *" />
                    <FormSelect id="category" :options="categories" :value="form.category" class="mt-1 block w-full" v-model="form.category" :reduce="category => category.value" />
                    <FormError v-if="form.hasErrors && form.errors.category" :message="form.errors.category" />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Pflichtfeld. Wählen Sie eine passende Rubrik. Eine Mehrfachauswahl ist nicht möglich.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="location" value="Ort *" />
                    <FormSelect id="location" :options="locations" :value="form.location" class="mt-1 block w-full" v-model="form.location" :reduce="location => location.value" />
                    <FormError v-if="form.hasErrors && form.errors.location" :message="form.errors.location" />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Tippen Sie mehrere Buchstaben Ihres Veranstaltungsortes in das Eingabefeld.
                    Das System listet mehrere Orte auf.
                    Wählen Sie Ihren Veranstaltungsort aus.
                    Ist Ihr Ort nicht vorhanden, senden Sie uns bitte eine Mail an <a href="mailto:kuka-manager@kulturkalender-dresden.de" class="whitespace-nowrap">kuka-manager@kulturkalender-dresden.de</a> mit Ihrem Vorschlag.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="name" value="Titel der Veranstaltung *" />
                    <FormInput id="name" type="text" class="mt-1 block w-full" v-model="form.name" required maxlength="100" placeholder="max. 100 Zeichen" />
                    <FormError v-if="form.hasErrors && form.errors.name" :message="form.errors.name" />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Tragen Sie einen aussagekräftigen Namen für Ihre Veranstaltung ein.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="title" value="Untertitel" />
                    <FormInput id="title" type="text" class="mt-1 block w-full" v-model="form.title" maxlength="100" placeholder="max. 100 Zeichen" />
                    <FormError v-if="form.hasErrors && form.errors.title" :message="form.errors.title" />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Tragen Sie eine Zusatzinformation zur Veranstaltung ein, die Ihre Veranstaltung interessanter macht, z.B. den Namen eines Künstlers.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="details" value="Internetseite" />
                    <FormInputUrl id="details" class="mt-1 block w-full" v-model="form.details" placeholder="https://" />
                    <FormError v-if="form.hasErrors && form.errors.details" :message="form.errors.details" />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Tragen Sie hier den vollständigen Link inkl. https:// zu Ihrer Internetseite ein. Ist kein Link vorhanden, wird automatisch ein Link zum Veranstaltungsort gesetzt.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="tickets" value="Tickets" />
                    <FormInputUrl id="tickets" class="mt-1 block w-full" v-model="form.tickets" placeholder="https://" />
                    <FormError v-if="form.hasErrors && form.errors.tickets" :message="form.errors.tickets" />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Verlinken Sie zu Ihrem Ticketshop oder auf die konkrete Ticketseite einer Verkaufsplattform z.B. Eventim, Reservix usw.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2">
                    <FormLabel for="teaser" value="Inhalte" />
                    <FormTextarea id="teaser" class="mt-1 block w-full" v-model="form.teaser" maxlength="3000" rows="4" placeholder="max. 3.000 Zeichen" />
                    <FormError v-if="form.hasErrors && form.errors.teaser" :message="form.errors.teaser" />
                </div>
                <div class="w-full lg:w-1/2 text-sm lg:pt-5">
                    Tragen Sie hier die Beschreibung zur Veranstaltung ein.
                </div>
            </div>
            <div class="w-full lg:flex items-start lg:space-x-8 mt-4">
                <div class="w-full lg:w-1/2 flex items-center justify-end space-x-4 mt-4">
                    <FormButton :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        Weiter zu Schritt 2
                    </FormButton>
                </div>
                <div class="w-full lg:w-1/2 text-sm">&nbsp;</div>
            </div>
        </form>
    </div>
</template>

<script setup>
import { useForm } from "@inertiajs/vue3";
import AppHead from '@/Shared/Head';
import PageTitle from '@/Shared/Page/Title';
import FormError from '@/Shared/Form/Error';
import FormLabel from '@/Shared/Form/Label';
import FormSelect from '@/Shared/Form/Select';
import FormInput from '@/Shared/Form/Input';
import FormTextarea from '@/Shared/Form/Textarea';
import FormButton from '@/Shared/Form/Button';
import FormInputUrl from '@/Shared/Form/InputUrl';

const props = defineProps({
    categories: Array,
    locations: Array,
    event: Object,
    date: Number,
})
const form = useForm({
    category: props.event?.category,
    location: props.event?.location,
    name: props.event?.name,
    title: props.event?.title,
    details: props.event?.details,
    tickets: props.event?.tickets,
    teaser: props.event?.teaser,
});
const submit = () => {
    form.post(route('manager.event.store', [props.event?.slug, props.date]), {
        preserveScroll: true
    });
};
</script>
